/**
 * Card content registry — the full NULLRUN card set.
 *
 * Every BASE card has an upgraded "+" entry (id + "+"). Cards are pure data,
 * interpreted by the effect engine, so the whole set is serializable + testable.
 *
 * Archetypes (tags):
 *   - "aggro"   Aggro/Spike: front-loaded damage, Strength scaling, burst.
 *   - "armor"   Armor/Counter: Block, Firewall, defense-as-offense.
 *   - "virus"   Virus/DoT: Virus/Poison stacking + spread/detonate payoffs.
 *   - "combo"   Chain/Combo: rewards playing many cards / sequencing a turn.
 *   - "draw"    Draw-engine/Economy: card flow + Cycle generation.
 *   - "control" Trace control + utility (heal, debuffs) that glues decks.
 *
 * Rarities: starter · common · uncommon · rare · special.
 *
 * Trace discipline: loud/powerful effects pay `traceOnPlay` (the aggression
 * tax). Cheap defensive/economy cards are quiet. This is the core tension.
 */

import type { CardDef } from "../types";

/**
 * Build a base+upgrade pair compactly. `up` is a partial overlay merged over the
 * base for the upgraded card (and name/description tweaks). Keeps the registry
 * readable while guaranteeing every card has an upgrade.
 */
function pair(
  base: Omit<CardDef, "upgradesTo" | "upgraded">,
  up: Partial<CardDef> & { name: string; description: string },
): [CardDef, CardDef] {
  const upId = `${base.id}+`;
  const baseDef: CardDef = { ...base, upgradesTo: upId };
  const upDef: CardDef = {
    ...base,
    ...up,
    id: upId,
    upgraded: true,
  };
  return [baseDef, upDef];
}

const PAIRS: [CardDef, CardDef][] = [
  // ===========================================================================
  // STARTERS
  // ===========================================================================
  pair(
    {
      id: "strike",
      name: "Strike",
      type: "attack",
      rarity: "starter",
      cost: 1,
      target: "enemy",
      effects: [{ kind: "damage", value: 6 }],
      tags: ["aggro"],
      description: "Deal 6 damage.",
    },
    { name: "Strike+", description: "Deal 9 damage.", effects: [{ kind: "damage", value: 9 }] },
  ),
  pair(
    {
      id: "defend",
      name: "Defend",
      type: "skill",
      rarity: "starter",
      cost: 1,
      target: "self",
      effects: [{ kind: "block", value: 5 }],
      tags: ["armor"],
      description: "Gain 5 Block.",
    },
    { name: "Defend+", description: "Gain 8 Block.", effects: [{ kind: "block", value: 8 }] },
  ),

  // ===========================================================================
  // AGGRO / SPIKE
  // ===========================================================================
  pair(
    {
      id: "overload",
      name: "Overload",
      type: "attack",
      rarity: "common",
      cost: 2,
      target: "enemy",
      effects: [{ kind: "damage", value: 16 }],
      traceOnPlay: 2,
      tags: ["aggro"],
      description: "Deal 16 damage. (Trace +2 — loud)",
    },
    {
      name: "Overload+",
      description: "Deal 22 damage. (Trace +2)",
      effects: [{ kind: "damage", value: 22 }],
    },
  ),
  pair(
    {
      id: "spike",
      name: "Spike",
      type: "attack",
      rarity: "common",
      cost: 1,
      target: "enemy",
      effects: [
        { kind: "damage", value: 4 },
        { kind: "damage", value: 0, scaleByStatus: "strength" },
      ],
      tags: ["aggro"],
      description: "Deal 4 damage, plus 1 per Strength (twice-scaled).",
    },
    {
      name: "Spike+",
      description: "Deal 6 damage, plus 1 per Strength (twice-scaled).",
      effects: [
        { kind: "damage", value: 6 },
        { kind: "damage", value: 0, scaleByStatus: "strength" },
      ],
    },
  ),
  pair(
    {
      id: "rampage",
      name: "Rampage",
      type: "power",
      rarity: "uncommon",
      cost: 1,
      target: "self",
      effects: [{ kind: "applyStatusSelf", status: "strength", amount: 2 }],
      tags: ["aggro"],
      description: "Gain 2 Strength.",
    },
    {
      name: "Rampage+",
      description: "Gain 3 Strength.",
      effects: [{ kind: "applyStatusSelf", status: "strength", amount: 3 }],
    },
  ),
  pair(
    {
      id: "hemorrhage",
      name: "Hemorrhage",
      type: "attack",
      rarity: "uncommon",
      cost: 1,
      target: "enemy",
      effects: [
        { kind: "damage", value: 8 },
        { kind: "loseHpSelf", value: 2 },
      ],
      tags: ["aggro"],
      description: "Deal 8 damage. Lose 2 HP.",
    },
    {
      name: "Hemorrhage+",
      description: "Deal 12 damage. Lose 2 HP.",
      effects: [
        { kind: "damage", value: 12 },
        { kind: "loseHpSelf", value: 2 },
      ],
    },
  ),
  pair(
    {
      id: "barrage",
      name: "Barrage",
      type: "attack",
      rarity: "uncommon",
      cost: 1,
      target: "enemy",
      effects: [{ kind: "damageMulti", value: 3, hits: 2 }],
      tags: ["aggro", "combo"],
      description: "Deal 3 damage 2 times.",
    },
    {
      name: "Barrage+",
      description: "Deal 3 damage 3 times.",
      effects: [{ kind: "damageMulti", value: 3, hits: 3 }],
    },
  ),
  pair(
    {
      id: "executable",
      name: "Executable",
      type: "attack",
      rarity: "rare",
      cost: 2,
      target: "enemy",
      effects: [{ kind: "damage", value: 10, scaleByCounter: "attacksPlayed" }],
      tags: ["aggro", "combo"],
      description: "Deal 10 damage, +1 per other attack played this turn.",
    },
    {
      name: "Executable+",
      description: "Deal 14 damage, +1 per other attack played this turn.",
      effects: [{ kind: "damage", value: 14, scaleByCounter: "attacksPlayed" }],
    },
  ),
  pair(
    {
      id: "zero_day",
      name: "Zero-Day",
      type: "attack",
      rarity: "rare",
      cost: 2,
      target: "enemy",
      effects: [{ kind: "damage", value: 24 }],
      traceOnPlay: 3,
      exhaust: true,
      tags: ["aggro"],
      description: "Deal 24 damage. Exhaust. (Trace +3 — very loud)",
    },
    {
      name: "Zero-Day+",
      description: "Deal 32 damage. Exhaust. (Trace +3)",
      effects: [{ kind: "damage", value: 32 }],
    },
  ),
  pair(
    {
      id: "berserk",
      name: "Berserk",
      type: "power",
      rarity: "rare",
      cost: 0,
      target: "self",
      effects: [
        { kind: "applyStatusSelf", status: "overclock", amount: 99 },
        { kind: "applyStatusSelf", status: "vulnerable", amount: 2 },
      ],
      traceOnPlay: 1,
      tags: ["aggro", "economy"],
      description: "Gain +1 Cycle every turn this combat. Become 2 Vulnerable now. (Trace +1)",
    },
    {
      name: "Berserk+",
      description: "Gain +1 Cycle every turn this combat. Become 1 Vulnerable now. (Trace +1)",
      effects: [
        { kind: "applyStatusSelf", status: "overclock", amount: 99 },
        { kind: "applyStatusSelf", status: "vulnerable", amount: 1 },
      ],
    },
  ),

  // ===========================================================================
  // ARMOR / COUNTER
  // ===========================================================================
  pair(
    {
      id: "barrier",
      name: "Barrier",
      type: "skill",
      rarity: "common",
      cost: 2,
      target: "self",
      effects: [
        { kind: "block", value: 8 },
        { kind: "applyStatusSelf", status: "dexterity", amount: 1 },
      ],
      tags: ["armor"],
      description: "Gain 8 Block and 1 Dexterity.",
    },
    {
      name: "Barrier+",
      description: "Gain 11 Block and 1 Dexterity.",
      effects: [
        { kind: "block", value: 11 },
        { kind: "applyStatusSelf", status: "dexterity", amount: 1 },
      ],
    },
  ),
  pair(
    {
      id: "hardening",
      name: "Hardening",
      type: "power",
      rarity: "uncommon",
      cost: 1,
      target: "self",
      effects: [{ kind: "applyStatusSelf", status: "firewall", amount: 2 }],
      tags: ["armor"],
      description: "Gain 2 Firewall (reduce all incoming hits by 2).",
    },
    {
      name: "Hardening+",
      description: "Gain 3 Firewall.",
      effects: [{ kind: "applyStatusSelf", status: "firewall", amount: 3 }],
    },
  ),
  pair(
    {
      id: "bulwark_stance",
      name: "Bulwark Stance",
      type: "skill",
      rarity: "common",
      cost: 1,
      target: "self",
      effects: [{ kind: "block", value: 9 }],
      tags: ["armor"],
      description: "Gain 9 Block.",
    },
    {
      name: "Bulwark Stance+",
      description: "Gain 13 Block.",
      effects: [{ kind: "block", value: 13 }],
    },
  ),
  pair(
    {
      id: "riposte",
      name: "Riposte",
      type: "attack",
      rarity: "uncommon",
      cost: 1,
      target: "enemy",
      effects: [
        { kind: "block", value: 5 },
        { kind: "loseBlockToDamage" },
      ],
      tags: ["armor", "counter"],
      description: "Gain 5 Block, then deal damage equal to your current Block.",
    },
    {
      name: "Riposte+",
      description: "Gain 8 Block, then deal damage equal to your current Block.",
      effects: [
        { kind: "block", value: 8 },
        { kind: "loseBlockToDamage" },
      ],
    },
  ),
  pair(
    {
      id: "deflect_grid",
      name: "Deflect Grid",
      type: "skill",
      rarity: "uncommon",
      cost: 1,
      target: "self",
      effects: [
        { kind: "block", value: 6 },
        { kind: "draw", value: 1 },
      ],
      tags: ["armor", "draw"],
      description: "Gain 6 Block. Draw 1 card.",
    },
    {
      name: "Deflect Grid+",
      description: "Gain 9 Block. Draw 1 card.",
      effects: [
        { kind: "block", value: 9 },
        { kind: "draw", value: 1 },
      ],
    },
  ),
  pair(
    {
      id: "phase_shift",
      name: "Phase Shift",
      type: "skill",
      rarity: "rare",
      cost: 1,
      target: "self",
      effects: [{ kind: "applyStatusSelf", status: "intangible", amount: 1 }],
      exhaust: true,
      tags: ["armor", "control"],
      description: "Become Intangible this turn (take only 1 from each hit). Exhaust.",
    },
    {
      name: "Phase Shift+",
      description: "Become Intangible this turn (take only 1 from each hit).",
      exhaust: false,
    },
  ),
  pair(
    {
      id: "fortify_core",
      name: "Fortify Core",
      type: "power",
      rarity: "rare",
      cost: 2,
      target: "self",
      effects: [
        { kind: "applyStatusSelf", status: "dexterity", amount: 2 },
        { kind: "applyStatusSelf", status: "firewall", amount: 1 },
      ],
      tags: ["armor"],
      description: "Gain 2 Dexterity and 1 Firewall.",
    },
    {
      name: "Fortify Core+",
      description: "Gain 3 Dexterity and 2 Firewall.",
      effects: [
        { kind: "applyStatusSelf", status: "dexterity", amount: 3 },
        { kind: "applyStatusSelf", status: "firewall", amount: 2 },
      ],
    },
  ),

  // ===========================================================================
  // VIRUS / DoT
  // ===========================================================================
  pair(
    {
      id: "infect",
      name: "Infect",
      type: "skill",
      rarity: "common",
      cost: 1,
      target: "enemy",
      effects: [{ kind: "applyStatus", status: "virus", amount: 4 }],
      traceOnPlay: 1,
      tags: ["virus"],
      description: "Apply 4 Virus. (Trace +1)",
    },
    {
      name: "Infect+",
      description: "Apply 6 Virus. (Trace +1)",
      effects: [{ kind: "applyStatus", status: "virus", amount: 6 }],
    },
  ),
  pair(
    {
      id: "contagion",
      name: "Contagion",
      type: "skill",
      rarity: "uncommon",
      cost: 2,
      target: "all-enemies",
      effects: [{ kind: "applyStatusAll", status: "poison", amount: 3 }],
      traceOnPlay: 2,
      tags: ["virus"],
      description: "Apply 3 Poison to ALL ICE. (Trace +2)",
    },
    {
      name: "Contagion+",
      description: "Apply 5 Poison to ALL ICE. (Trace +2)",
      effects: [{ kind: "applyStatusAll", status: "poison", amount: 5 }],
    },
  ),
  pair(
    {
      id: "corrode",
      name: "Corrode",
      type: "skill",
      rarity: "common",
      cost: 1,
      target: "enemy",
      effects: [{ kind: "applyStatus", status: "poison", amount: 5 }],
      tags: ["virus"],
      description: "Apply 5 Poison.",
    },
    {
      name: "Corrode+",
      description: "Apply 8 Poison.",
      effects: [{ kind: "applyStatus", status: "poison", amount: 8 }],
    },
  ),
  pair(
    {
      id: "vector",
      name: "Vector",
      type: "skill",
      rarity: "uncommon",
      cost: 1,
      target: "enemy",
      effects: [{ kind: "spreadStatus", status: "virus" }],
      traceOnPlay: 1,
      tags: ["virus", "combo"],
      description: "Copy the target's Virus to every other ICE. (Trace +1)",
    },
    {
      name: "Vector+",
      description: "Copy the target's Virus AND Poison to every other ICE. (Trace +1)",
      effects: [
        { kind: "spreadStatus", status: "virus" },
        { kind: "spreadStatus", status: "poison" },
      ],
    },
  ),
  pair(
    {
      id: "detonate",
      name: "Detonate",
      type: "attack",
      rarity: "rare",
      cost: 1,
      target: "enemy",
      effects: [
        { kind: "damage", value: 4 },
        { kind: "detonateStatus", status: "poison" },
      ],
      tags: ["virus", "aggro"],
      description: "Deal 4 damage, then consume all Poison on the target as damage.",
    },
    {
      name: "Detonate+",
      description: "Deal 7 damage, then consume all Poison on the target as damage.",
      effects: [
        { kind: "damage", value: 7 },
        { kind: "detonateStatus", status: "poison" },
      ],
    },
  ),
  pair(
    {
      id: "weaponized_strain",
      name: "Weaponized Strain",
      type: "power",
      rarity: "rare",
      cost: 1,
      target: "self",
      effects: [{ kind: "gainCardTemp", cardDefId: "corrode", count: 2 }],
      tags: ["virus"],
      description: "Add 2 Corrode to your hand.",
    },
    {
      name: "Weaponized Strain+",
      description: "Add 3 Corrode to your hand.",
      effects: [{ kind: "gainCardTemp", cardDefId: "corrode", count: 3 }],
    },
  ),
  pair(
    {
      id: "blight",
      name: "Blight",
      type: "skill",
      rarity: "uncommon",
      cost: 2,
      target: "all-enemies",
      effects: [{ kind: "applyStatusAll", status: "virus", amount: 5 }],
      traceOnPlay: 2,
      tags: ["virus"],
      description: "Apply 5 Virus to ALL ICE. (Trace +2)",
    },
    {
      name: "Blight+",
      description: "Apply 7 Virus to ALL ICE. (Trace +2)",
      effects: [{ kind: "applyStatusAll", status: "virus", amount: 7 }],
    },
  ),

  // ===========================================================================
  // CHAIN / COMBO
  // ===========================================================================
  pair(
    {
      id: "cascade",
      name: "Cascade",
      type: "attack",
      rarity: "common",
      cost: 1,
      target: "enemy",
      effects: [{ kind: "damage", value: 3, scaleByCounter: "cardsPlayed" }],
      tags: ["combo"],
      description: "Deal 3 damage, +1 per other card played this turn.",
    },
    {
      name: "Cascade+",
      description: "Deal 5 damage, +1 per other card played this turn.",
      effects: [{ kind: "damage", value: 5, scaleByCounter: "cardsPlayed" }],
    },
  ),
  pair(
    {
      id: "relay",
      name: "Relay",
      type: "skill",
      rarity: "common",
      cost: 0,
      target: "none",
      effects: [{ kind: "draw", value: 1 }],
      tags: ["combo", "draw"],
      description: "Draw 1 card. (Free — keeps the chain going.)",
    },
    {
      name: "Relay+",
      description: "Draw 2 cards. (Free.)",
      effects: [{ kind: "draw", value: 2 }],
    },
  ),
  pair(
    {
      id: "sequence",
      name: "Sequence",
      type: "skill",
      rarity: "uncommon",
      cost: 1,
      target: "self",
      effects: [{ kind: "block", value: 2, scaleByCounter: "cardsPlayed" }],
      tags: ["combo", "armor"],
      description: "Gain 2 Block, +2 per other card played this turn.",
    },
    {
      name: "Sequence+",
      description: "Gain 4 Block, +2 per other card played this turn.",
      effects: [{ kind: "block", value: 4, scaleByCounter: "cardsPlayed" }],
    },
  ),
  pair(
    {
      id: "fork_bomb",
      name: "Fork Bomb",
      type: "skill",
      rarity: "rare",
      cost: 1,
      target: "none",
      effects: [{ kind: "gainCardTemp", cardDefId: "relay", count: 3 }],
      exhaust: true,
      tags: ["combo", "draw"],
      description: "Add 3 Relay to your hand. Exhaust.",
    },
    {
      name: "Fork Bomb+",
      description: "Add 4 Relay to your hand. Exhaust.",
      effects: [{ kind: "gainCardTemp", cardDefId: "relay", count: 4 }],
    },
  ),
  pair(
    {
      id: "combo_breaker",
      name: "Combo Breaker",
      type: "attack",
      rarity: "uncommon",
      cost: 2,
      target: "enemy",
      effects: [{ kind: "damageMulti", value: 4, hits: 3 }],
      traceOnPlay: 1,
      tags: ["combo", "aggro"],
      description: "Deal 4 damage 3 times. (Trace +1)",
    },
    {
      name: "Combo Breaker+",
      description: "Deal 5 damage 3 times. (Trace +1)",
      effects: [{ kind: "damageMulti", value: 5, hits: 3 }],
    },
  ),

  // ===========================================================================
  // DRAW ENGINE / ECONOMY
  // ===========================================================================
  pair(
    {
      id: "bandwidth",
      name: "Bandwidth",
      type: "skill",
      rarity: "common",
      cost: 1,
      target: "none",
      effects: [{ kind: "draw", value: 2 }],
      tags: ["draw"],
      description: "Draw 2 cards.",
    },
    {
      name: "Bandwidth+",
      description: "Draw 3 cards.",
      effects: [{ kind: "draw", value: 3 }],
    },
  ),
  pair(
    {
      id: "overclock",
      name: "Overclock",
      type: "power",
      rarity: "uncommon",
      cost: 0,
      target: "self",
      effects: [{ kind: "applyStatusSelf", status: "overclock", amount: 3 }],
      traceOnPlay: 1,
      tags: ["draw", "economy"],
      description: "Gain +1 Cycle/turn for 3 turns. (Trace +1)",
    },
    {
      name: "Overclock+",
      description: "Gain +1 Cycle/turn for 5 turns. (Trace +1)",
      effects: [{ kind: "applyStatusSelf", status: "overclock", amount: 5 }],
    },
  ),
  pair(
    {
      id: "inject",
      name: "Inject Cycles",
      type: "skill",
      rarity: "uncommon",
      cost: 0,
      target: "none",
      effects: [
        { kind: "gainCycles", value: 2 },
        { kind: "loseHpSelf", value: 3 },
      ],
      traceOnPlay: 1,
      exhaust: true,
      tags: ["economy"],
      description: "Gain 2 Cycles. Lose 3 HP. Exhaust. (Trace +1)",
    },
    {
      name: "Inject Cycles+",
      description: "Gain 3 Cycles. Lose 3 HP. Exhaust. (Trace +1)",
      effects: [
        { kind: "gainCycles", value: 3 },
        { kind: "loseHpSelf", value: 3 },
      ],
    },
  ),
  pair(
    {
      id: "datamine",
      name: "Datamine",
      type: "skill",
      rarity: "common",
      cost: 1,
      target: "none",
      effects: [
        { kind: "draw", value: 1 },
        { kind: "gainCycles", value: 1 },
      ],
      tags: ["draw", "economy"],
      description: "Draw 1 card and gain 1 Cycle.",
    },
    {
      name: "Datamine+",
      description: "Draw 2 cards and gain 1 Cycle.",
      effects: [
        { kind: "draw", value: 2 },
        { kind: "gainCycles", value: 1 },
      ],
    },
  ),
  pair(
    {
      id: "recompile",
      name: "Recompile",
      type: "skill",
      rarity: "uncommon",
      cost: 1,
      target: "none",
      effects: [
        { kind: "draw", value: 2 },
        { kind: "reduceTrace", value: 2 },
      ],
      tags: ["draw", "control"],
      description: "Draw 2 cards. Reduce Trace by 2.",
    },
    {
      name: "Recompile+",
      description: "Draw 3 cards. Reduce Trace by 3.",
      effects: [
        { kind: "draw", value: 3 },
        { kind: "reduceTrace", value: 3 },
      ],
    },
  ),

  // ===========================================================================
  // DEBUFFS / CONTROL
  // ===========================================================================
  pair(
    {
      id: "exploit",
      name: "Exploit",
      type: "skill",
      rarity: "common",
      cost: 1,
      target: "enemy",
      effects: [{ kind: "applyStatus", status: "vulnerable", amount: 2 }],
      tags: ["control", "debuff"],
      description: "Apply 2 Vulnerable.",
    },
    {
      name: "Exploit+",
      description: "Apply 3 Vulnerable.",
      effects: [{ kind: "applyStatus", status: "vulnerable", amount: 3 }],
    },
  ),
  pair(
    {
      id: "jam",
      name: "Jam Signal",
      type: "skill",
      rarity: "common",
      cost: 1,
      target: "enemy",
      effects: [{ kind: "applyStatus", status: "weak", amount: 2 }],
      tags: ["control", "debuff"],
      description: "Apply 2 Weak.",
    },
    {
      name: "Jam Signal+",
      description: "Apply 3 Weak.",
      effects: [{ kind: "applyStatus", status: "weak", amount: 3 }],
    },
  ),
  pair(
    {
      id: "crack",
      name: "Crack",
      type: "attack",
      rarity: "uncommon",
      cost: 1,
      target: "enemy",
      effects: [
        { kind: "damage", value: 5 },
        { kind: "applyStatus", status: "exposed", amount: 2 },
      ],
      tags: ["debuff", "aggro"],
      description: "Deal 5 damage. Apply 2 Exposed (permanent +damage taken).",
    },
    {
      name: "Crack+",
      description: "Deal 7 damage. Apply 3 Exposed (permanent).",
      effects: [
        { kind: "damage", value: 7 },
        { kind: "applyStatus", status: "exposed", amount: 3 },
      ],
    },
  ),
  pair(
    {
      id: "lockpick",
      name: "Lockpick",
      type: "skill",
      rarity: "uncommon",
      cost: 0,
      target: "enemy",
      effects: [
        { kind: "applyStatus", status: "vulnerable", amount: 1 },
        { kind: "applyStatus", status: "weak", amount: 1 },
      ],
      tags: ["control", "debuff"],
      description: "Apply 1 Vulnerable and 1 Weak.",
    },
    {
      name: "Lockpick+",
      description: "Apply 2 Vulnerable and 2 Weak.",
      effects: [
        { kind: "applyStatus", status: "vulnerable", amount: 2 },
        { kind: "applyStatus", status: "weak", amount: 2 },
      ],
    },
  ),
  pair(
    {
      id: "scrub",
      name: "Scrub Logs",
      type: "skill",
      rarity: "uncommon",
      cost: 1,
      target: "none",
      effects: [{ kind: "reduceTrace", value: 4 }],
      exhaust: true,
      tags: ["control"],
      description: "Reduce Trace by 4. Exhaust.",
    },
    {
      name: "Scrub Logs+",
      description: "Reduce Trace by 7. Exhaust.",
      effects: [{ kind: "reduceTrace", value: 7 }],
    },
  ),
  pair(
    {
      id: "ghostwalk",
      name: "Ghostwalk",
      type: "skill",
      rarity: "rare",
      cost: 1,
      target: "none",
      effects: [
        { kind: "reduceTrace", value: 3 },
        { kind: "applyStatusSelf", status: "intangible", amount: 1 },
      ],
      exhaust: true,
      tags: ["control", "armor"],
      description: "Reduce Trace by 3. Become Intangible this turn. Exhaust.",
    },
    {
      name: "Ghostwalk+",
      description: "Reduce Trace by 5. Become Intangible this turn. Exhaust.",
      effects: [
        { kind: "reduceTrace", value: 5 },
        { kind: "applyStatusSelf", status: "intangible", amount: 1 },
      ],
    },
  ),

  // ===========================================================================
  // UTILITY / HEAL / AOE
  // ===========================================================================
  pair(
    {
      id: "patch",
      name: "Patch",
      type: "skill",
      rarity: "common",
      cost: 1,
      target: "self",
      effects: [{ kind: "heal", value: 4 }],
      exhaust: true,
      tags: ["control"],
      description: "Heal 4 HP. Exhaust.",
    },
    {
      name: "Patch+",
      description: "Heal 7 HP. Exhaust.",
      effects: [{ kind: "heal", value: 7 }],
    },
  ),
  pair(
    {
      id: "emp",
      name: "EMP Burst",
      type: "attack",
      rarity: "uncommon",
      cost: 2,
      target: "all-enemies",
      effects: [{ kind: "damageAll", value: 8 }],
      traceOnPlay: 2,
      tags: ["aggro"],
      description: "Deal 8 damage to ALL ICE. (Trace +2)",
    },
    {
      name: "EMP Burst+",
      description: "Deal 12 damage to ALL ICE. (Trace +2)",
      effects: [{ kind: "damageAll", value: 12 }],
    },
  ),
  pair(
    {
      id: "shockwave",
      name: "Shockwave",
      type: "attack",
      rarity: "uncommon",
      cost: 2,
      target: "all-enemies",
      effects: [
        { kind: "damageAll", value: 4 },
        { kind: "applyStatusAll", status: "weak", amount: 1 },
      ],
      traceOnPlay: 1,
      tags: ["control", "aggro"],
      description: "Deal 4 to ALL ICE and apply 1 Weak to all. (Trace +1)",
    },
    {
      name: "Shockwave+",
      description: "Deal 6 to ALL ICE and apply 2 Weak to all. (Trace +1)",
      effects: [
        { kind: "damageAll", value: 6 },
        { kind: "applyStatusAll", status: "weak", amount: 2 },
      ],
    },
  ),
  pair(
    {
      id: "regen_loop",
      name: "Regen Loop",
      type: "power",
      rarity: "uncommon",
      cost: 1,
      target: "self",
      effects: [{ kind: "applyStatusSelf", status: "regen", amount: 4 }],
      tags: ["control"],
      description: "Gain 4 Regen (heal, decaying, at the start of your turns).",
    },
    {
      name: "Regen Loop+",
      description: "Gain 6 Regen.",
      effects: [{ kind: "applyStatusSelf", status: "regen", amount: 6 }],
    },
  ),
];

// Build the registry from the pairs, plus the special/curse cards (no upgrades).
export const CARD_DEFS: Record<string, CardDef> = (() => {
  const defs: Record<string, CardDef> = {};
  for (const [base, up] of PAIRS) {
    defs[base.id] = base;
    defs[up.id] = up;
  }

  // Status/curse cards: unplayable hand-clutter generated by tough ICE/events.
  defs.glitch = {
    id: "glitch",
    name: "Glitch",
    type: "status",
    rarity: "special",
    cost: 0,
    target: "none",
    effects: [],
    unplayable: true,
    description: "Unplayable. Clutters your hand.",
  };
  defs.corruption = {
    id: "corruption",
    name: "Corruption",
    type: "curse",
    rarity: "special",
    cost: 0,
    target: "none",
    effects: [],
    unplayable: true,
    description: "Unplayable curse. Take 1 HP damage at the start of each turn while in hand.",
  };

  return defs;
})();

/** Get a card def or throw (callers should pass valid ids). */
export function getCardDef(id: string): CardDef {
  const def = CARD_DEFS[id];
  if (!def) throw new Error(`Unknown card def: ${id}`);
  return def;
}

/** All card ids (stable, sorted). */
export function allCardIds(): string[] {
  return Object.keys(CARD_DEFS).sort();
}

/**
 * Cards eligible for combat/treasure reward pools: non-starter, non-special,
 * non-upgraded, playable. The pool the run draws choices from.
 */
export function rewardCardIds(): string[] {
  return allCardIds().filter((id) => {
    const d = CARD_DEFS[id]!;
    return (
      !d.upgraded &&
      !d.unplayable &&
      d.rarity !== "starter" &&
      d.rarity !== "special"
    );
  });
}
